import { Component, inject, Input, ChangeDetectionStrategy } from '@angular/core'
import { CommonModule } from '@angular/common'
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay'
import { MatIcon } from '@angular/material/icon'
import { TranslatePipe } from '@ngx-translate/core'
import { MeetingHealthItem } from './meeting-health.utils'
import { IntercomService } from '../intercom.service'

@Component({
  selector: 'app-meeting-health-indicator',
  standalone: true,
  imports: [CommonModule, CdkConnectedOverlay, CdkOverlayOrigin, MatIcon, TranslatePipe],
  templateUrl: './meeting-health-indicator.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meeting-health-indicator.component.scss',
})
export class MeetingHealthIndicatorComponent {
  intercomService = inject(IntercomService)
  @Input()
  userActionsItems: MeetingHealthItem[] = []

  @Input()
  avgConfidenceScore: number | null = null

  isTooltipOpen = false
  isTooltipPinned = false
  private closeTimeout: any = null

  get sortedItems(): MeetingHealthItem[] {
    return [...this.userActionsItems].sort((a, b) => {
      if (a.isComplete === b.isComplete) return 0
      return a.isComplete ? 1 : -1
    })
  }

  get completedCount(): number {
    return this.userActionsItems.filter((item) => item.isComplete).length
  }

  get totalCount(): number {
    return this.userActionsItems.length
  }

  get remainingCount(): number {
    return this.totalCount - this.completedCount
  }

  get isComplete(): boolean {
    return this.remainingCount === 0
  }

  get fillPercentage(): number {
    if (this.totalCount === 0 || this.completedCount === 0) {
      return 10
    }
    return Math.round((this.completedCount / this.totalCount) * 100)
  }

  get strokeDashoffset(): number {
    const radius = 15
    const circumference = 2 * Math.PI * radius
    return circumference - (this.fillPercentage / 100) * circumference
  }

  get strokeDashoffsetSmall(): number {
    const radius = 10.5
    const circumference = 2 * Math.PI * radius
    return circumference - (this.fillPercentage / 100) * circumference
  }

  toggleTooltip(): void {
    this.isTooltipPinned = !this.isTooltipPinned
    this.isTooltipOpen = this.isTooltipPinned
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout)
      this.closeTimeout = null
    }
  }

  closeTooltipFromBackdrop(): void {
    this.isTooltipPinned = false
    this.isTooltipOpen = false
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout)
      this.closeTimeout = null
    }
  }

  openTooltip(): void {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout)
      this.closeTimeout = null
    }
    this.isTooltipOpen = true
  }

  scheduleClose(): void {
    if (this.isTooltipPinned) {
      return
    }
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout)
    }
    this.closeTimeout = setTimeout(() => {
      if (!this.isTooltipPinned) {
        this.isTooltipOpen = false
      }
    }, 100)
  }

  closeTooltip(): void {
    if (!this.isTooltipPinned) {
      this.isTooltipOpen = false
    }
  }
}
